// Externe Bibliotheken
import React, { ReactNode, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

// Interne Module
import * as storage from '../_helper/storage';
import BookingData, { type BookingState, isBookingState } from '@/model/common/BookingData';
import { resolveTravelType } from '@/_helper/bookingHelper';
import { getBookingState } from '@/_helper/bookingDetailsHelper';
import { STORAGE_NAME_DEBUG_BOOKING_STATE, STORAGE_NAME_DEBUG_MODE } from '@/constants/debug';
import { isInternalIp } from '@/_helper/dynamicTestBooking';

export type AppContextProps = {
    isAffiliate: boolean;
    isLoggedIn: boolean;
    isDebugMode: boolean;
    bookingData: BookingData | null;
    saveBookingData: (data: BookingData) => void;
    constants: {
        MY_TRIP: string;
        BACK_TO_MY_TRIP: string;
    };
    travelType: string;
    bookingState?: BookingState;
    bookingStatus?: string;
    isMediated: boolean;
    isPostPay: boolean;
    logout: (autologout: boolean) => void;
};
export const AppContext = React.createContext<AppContextProps>({
    isAffiliate: false,
    isLoggedIn: false,
    isDebugMode: false,
    bookingData: null,
    saveBookingData: () => {},
    constants: {
        MY_TRIP: '',
        BACK_TO_MY_TRIP: '',
    },
    travelType: '',
    isMediated: false,
    isPostPay: false,
    logout: () => {},
});

type AppProviderProps = {
    children?: ReactNode;
    isAffiliate?: boolean;
};

export const AppProvider = ({ children, isAffiliate = false }: AppProviderProps) => {
    const [bookingData, setBookingData] = useState<BookingData>(storage.get('bookingData'));
    const navigate = useNavigate();
    const isDebugMode = Boolean(storage.get(STORAGE_NAME_DEBUG_MODE));
    const isInternal = isInternalIp();

    const travelType = useMemo(() => resolveTravelType(bookingData?.data) ?? '', [bookingData]);

    const isLoggedIn = useMemo(() => storage.get('login'), []);

    const saveBookingData = (data: BookingData) => {
        setBookingData(data);
        storage.set('bookingData', data);
    };

    const isMediated = useMemo(
        () => Object.values(bookingData?.data?.frontendParams?.mediatedServices ?? {}).some(Boolean),
        [bookingData]
    );

    const isPostPay = useMemo(
        () => Object.values(bookingData?.data?.frontendParams?.postPayServices ?? {}).some(Boolean),
        [bookingData]
    );

    const constants = {
        MY_TRIP: travelType === 'cruise' ? 'Meine Kreuzfahrt' : 'Meine Reise',
        BACK_TO_MY_TRIP: travelType === 'cruise' ? 'Zurück zu meiner Kreuzfahrt' : 'Zurück zu meiner Reise',
    };

    const bookingState = useMemo(() => {
        const debugBookingState = storage.get(STORAGE_NAME_DEBUG_BOOKING_STATE);
        return isInternal && debugBookingState && isBookingState(debugBookingState)
            ? debugBookingState
            : getBookingState(bookingData);
    }, [bookingData]);

    const bookingStatus = useMemo(() => bookingData?.data?.serviceInformation?.package?.state, [bookingData]);

    const logout = (autologout: boolean) => {
        storage.clear();
        navigate('/logout', { state: { autologout: autologout } });
    };

    return (
        <AppContext.Provider
            value={{
                isLoggedIn,
                isDebugMode,
                isAffiliate,
                bookingData,
                saveBookingData,
                constants,
                travelType,
                bookingState,
                bookingStatus,
                isMediated,
                isPostPay,
                logout,
            }}>
            {children}
        </AppContext.Provider>
    );
};
